import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Bell, BellRing, Flame, CreditCard, Package, CheckCircle2, Send, MessageCircle, Sparkles, Handshake } from 'lucide-react';
import SellerAdviceModal from '../components/shared/SellerAdviceModal';

const ICONS = { HOT_LEAD: Flame, STOCK_WAITLIST: BellRing, PAYMENT_PENDING: CreditCard, LOW_STOCK: Package, ORDER_CONFIRMED: CheckCircle2, BROADCAST_COMPLETE: Send, WHATSAPP_CONNECTED: MessageCircle, SELLER_HANDOFF: Handshake, SYSTEM: Bell };
const COLORS = { HOT_LEAD: '#FF6B35', STOCK_WAITLIST: '#0EA5E9', PAYMENT_PENDING: '#F59E0B', LOW_STOCK: '#EF4444', ORDER_CONFIRMED: '#00C896', BROADCAST_COMPLETE: '#6C5CE7', WHATSAPP_CONNECTED: '#25D366', SELLER_HANDOFF: '#8B5CF6', SYSTEM: '#9191A8' };

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [adviceConv, setAdviceConv] = useState(null);
  const [handoffMsg, setHandoffMsg] = useState({});
  // Oct 2026 (ticket 266314): seller-approved discount on a high-value escalation.
  const [discountPct, setDiscountPct] = useState({});
  const [discountMsg, setDiscountMsg] = useState({});
  const [discountBusy, setDiscountBusy] = useState({});
  const load = () => api.get('/notifications').then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  const readAll = async () => { await api.post('/notifications/read-all'); load(); };
  const readOne = async (id) => { await api.post(`/notifications/${id}/read`); load(); };
  // Ticket 266314 (round 8): act on THIS notification's issue when it has one; both
  // buttons send the buyer a fixed message (0 AI). Only Resolved resets Q1-Q4.
  const handoffAction = async (buyerKey, action, nid, issueId) => {
    try {
      const bk = encodeURIComponent(buyerKey);
      await api.post(issueId ? `/handoffs/${bk}/issues/${encodeURIComponent(issueId)}/${action}` : `/handoffs/${bk}/${action}`);
      setHandoffMsg((m) => ({ ...m, [nid]: action === 'resolve'
        ? 'Resolved — the buyer has been told, and their Q1–Q4 count starts again.'
        : "Marked 'I will follow up' — the buyer has been told you'll follow up." }));
      load();
    } catch (e) {
      setHandoffMsg((m) => ({ ...m, [nid]: 'Action failed, please retry.' }));
    }
  };

  const approveDiscount = async (n) => {
    const cid = n.conversation_id || n.payload?.conversation_id;
    const pct = parseFloat(discountPct[n.id]);
    if (!(pct > 0 && pct < 100)) {
      setDiscountMsg((m) => ({ ...m, [n.id]: 'Enter a percentage greater than 0 and less than 100.' }));
      return;
    }
    setDiscountBusy((b) => ({ ...b, [n.id]: true }));
    try {
      const { data } = await api.post(`/ai/conversations/${cid}/approve-discount`, { percent: pct });
      setDiscountMsg((m) => ({ ...m, [n.id]: `Approved ${data.discount_percent}% — new total ₹${data.total}. The buyer has been sent the updated total.` }));
      load();
    } catch (e) {
      setDiscountMsg((m) => ({ ...m, [n.id]: e?.response?.data?.detail || 'Could not approve the discount, please retry.' }));
    } finally {
      setDiscountBusy((b) => ({ ...b, [n.id]: false }));
    }
  };

  return (
    <div className="space-y-4 fade-up max-w-3xl">
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-fab-text-muted">{items.filter(n => !n.is_read).length} unread</div>
        <button onClick={readAll} className="text-[12px] text-fab-primary font-semibold">Mark all as read</button>
      </div>
      <div className="space-y-2">
        {items.map((n) => {
          const Icon = ICONS[n.type] || Bell;
          return (
            <div key={n.id} onClick={() => readOne(n.id)} className={`fab-card p-4 flex items-start gap-3 cursor-pointer hover:shadow-md transition-shadow ${!n.is_read ? 'border-l-4' : ''}`} style={!n.is_read ? { borderLeftColor: COLORS[n.type] } : {}}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: COLORS[n.type] + '20' }}>
                <Icon className="w-5 h-5" style={{ color: COLORS[n.type] }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-fab-text">{n.title}</div>
                <div className="text-[12px] text-fab-text-secondary">{n.body}</div>
                <div className="text-[10px] text-fab-text-muted mt-1">{new Date(n.created_at).toLocaleString()}</div>
                {(n.type === 'HOT_LEAD' || n.type === 'STOCK_WAITLIST') && n.resolved_at && (
                  <div className="mt-2 text-[12px] font-semibold text-fab-success" data-testid={`handled-${n.id}`}>✓ Handled — reply sent to the buyer</div>
                )}
                {(n.type === 'HOT_LEAD' || n.type === 'STOCK_WAITLIST') && !n.resolved_at && (n.conversation_id || n.payload?.conversation_id) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setAdviceConv(n.conversation_id || n.payload?.conversation_id); }}
                    data-testid={`reply-to-ai-${n.id}`}
                    className="btn-primary mt-2 inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> Reply to buyer
                  </button>
                )}
                {n.type === 'HOT_LEAD' && !n.resolved_at && n.payload?.escalation_type === 'high_value' && (n.conversation_id || n.payload?.conversation_id) && (
                  <div className="mt-2 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="number" min="0" max="99" step="0.5" placeholder="%"
                      value={discountPct[n.id] || ''}
                      onChange={(e) => setDiscountPct((m) => ({ ...m, [n.id]: e.target.value }))}
                      data-testid={`discount-pct-${n.id}`}
                      className="w-20 text-[12px] px-2 py-1.5 rounded-lg border border-fab-border" />
                    <button
                      onClick={() => approveDiscount(n)}
                      disabled={!!discountBusy[n.id]}
                      data-testid={`discount-approve-${n.id}`}
                      className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg border border-fab-border text-fab-text font-semibold hover:bg-fab-bg-soft disabled:opacity-50">
                      Approve discount
                    </button>
                    {discountMsg[n.id] && <span className="text-[11px] text-fab-text-muted">{discountMsg[n.id]}</span>}
                  </div>
                )}
                {n.type === 'SELLER_HANDOFF' && n.handoff_status === 'resolved' && (
                  <div className="mt-2 text-[12px] font-semibold text-fab-success" data-testid={`handoff-resolved-${n.id}`}>✓ Resolved</div>
                )}
                {n.type === 'SELLER_HANDOFF' && n.buyer_key && n.handoff_status !== 'resolved' && (
                  <div className="mt-2 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {n.handoff_status === 'following_up' && (
                      <span className="text-[11px] font-semibold px-2 py-1 rounded-md bg-fab-bg-soft text-fab-text-secondary" data-testid={`handoff-following-${n.id}`}>Following up</span>
                    )}
                    <button
                      onClick={() => handoffAction(n.buyer_key, 'follow-up', n.id, n.issue_id)}
                      data-testid={`handoff-followup-${n.id}`}
                      className="btn-primary inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5">
                      I will follow up
                    </button>
                    <button
                      onClick={() => handoffAction(n.buyer_key, 'resolve', n.id, n.issue_id)}
                      data-testid={`handoff-resolve-${n.id}`}
                      className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg border border-fab-border text-fab-text font-semibold hover:bg-fab-bg-soft">
                      Resolved
                    </button>
                    {n.conversation_id && (
                      <button
                        onClick={() => { setAdviceConv(n.conversation_id); }}
                        data-testid={`handoff-reply-${n.id}`}
                        className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg border border-fab-border text-fab-text font-semibold hover:bg-fab-bg-soft">
                        Reply to buyer
                      </button>
                    )}
                    {handoffMsg[n.id] && <span className="text-[11px] text-fab-text-muted">{handoffMsg[n.id]}</span>}
                  </div>
                )}
              </div>
              {!n.is_read && <span className="w-2 h-2 rounded-full bg-fab-primary" />}
            </div>
          );
        })}
        {items.length === 0 && <div className="text-center text-fab-text-muted py-10">No notifications yet.</div>}
      </div>
      {adviceConv && (
        <SellerAdviceModal
          conversationId={adviceConv}
          onClose={() => setAdviceConv(null)}
          onResolved={() => load()}
        />
      )}
    </div>
  );
}
